import { Injectable, InjectionToken } from '@angular/core'

import { Store } from '@ngrx/store'
import { Observable } from 'rxjs'

import { EAggregationAnalysisType, IAggregationAnalysisQuarter, IAggregationAnalysisSector } from '~/core/models'

import * as AGGREGATION_ANALYSIS_ACTIONS from './aggregation-analysis.actions'
import {
  selectAggregationAnalysisQuarter,
  selectAggregationAnalysisQuarterData,
  selectAggregationAnalysisQuarterError,
  selectAggregationAnalysisQuarterInRequest,
  selectAggregationAnalysisQuarterSelected,
  selectAggregationAnalysisSector,
  selectAggregationAnalysisSectorData,
  selectAggregationAnalysisSectorError,
  selectAggregationAnalysisSectorInRequest,
  selectAggregationAnalysisSectorOrder,
  selectAggregationAnalysisSectorSortBy,
  selectAggregationAnalysisType,
} from './aggregation-analysis.selectors'
import {
  IAggregationAnalysisQuarterState,
  IAggregationAnalysisSectorState,
  IAppWithAggregationAnalysisState,
} from './aggregation-analysis.state'

export interface IAggregationAnalysisFacade {
  // quarter
  quarter$: Observable<IAggregationAnalysisQuarterState>
  quarterData$: Observable<IAggregationAnalysisQuarter[]>
  quarterInRequest$: Observable<boolean>
  quarterError$: Observable<Error | undefined>
  quarterSelected$: Observable<IAggregationAnalysisQuarter | undefined>
  // sector
  sector$: Observable<IAggregationAnalysisSectorState>
  sectorData$: Observable<IAggregationAnalysisSector[]>
  sectorInRequest$: Observable<boolean>
  sectorError$: Observable<Error | undefined>
  sectorSortBy$: Observable<string>
  sectorOrder$: Observable<'asc' | 'desc' | ''>
  // aggregation type
  aggType$: Observable<EAggregationAnalysisType>

  // quarter
  getAggregationAnalysisQuarterData(): void
  changeAggregationAnalysisQuarterSelected(quarter: IAggregationAnalysisQuarter): void
  // sector
  changeAggregationAnalysisSectorSort(sortBy: string, order: 'asc' | 'desc' | ''): void
  // aggregation type
  changeAggregationAnalysisType(aggType: EAggregationAnalysisType): void
}

export const AGGREGATION_ANALYSIS_FACADE = new InjectionToken<IAggregationAnalysisFacade>('AGGREGATION_ANALYSIS_FACADE')

@Injectable()
export class AggregationAnalysisFacade implements IAggregationAnalysisFacade {
  constructor(private store$: Store<IAppWithAggregationAnalysisState>) {}

  // quarter
  quarter$ = this.store$.select(selectAggregationAnalysisQuarter)
  quarterData$ = this.store$.select(selectAggregationAnalysisQuarterData)
  quarterInRequest$ = this.store$.select(selectAggregationAnalysisQuarterInRequest)
  quarterError$ = this.store$.select(selectAggregationAnalysisQuarterError)
  quarterSelected$ = this.store$.select(selectAggregationAnalysisQuarterSelected)
  // sector
  sector$ = this.store$.select(selectAggregationAnalysisSector)
  sectorData$ = this.store$.select(selectAggregationAnalysisSectorData)
  sectorInRequest$ = this.store$.select(selectAggregationAnalysisSectorInRequest)
  sectorError$ = this.store$.select(selectAggregationAnalysisSectorError)
  sectorSortBy$ = this.store$.select(selectAggregationAnalysisSectorSortBy)
  sectorOrder$ = this.store$.select(selectAggregationAnalysisSectorOrder)
  // aggregation type
  aggType$ = this.store$.select(selectAggregationAnalysisType)

  // quarter
  getAggregationAnalysisQuarterData(): void {
    this.store$.dispatch(AGGREGATION_ANALYSIS_ACTIONS.getAggregationAnalysisQuarterData())
  }

  changeAggregationAnalysisQuarterSelected(quarter: IAggregationAnalysisQuarter): void {
    this.store$.dispatch(AGGREGATION_ANALYSIS_ACTIONS.setAggregationAnalysisQuarterSelected({ selected: quarter }))
  }

  // sector
  changeAggregationAnalysisSectorSort(sortBy: string, order: 'asc' | 'desc' | ''): void {
    this.store$.dispatch(AGGREGATION_ANALYSIS_ACTIONS.setAggregationAnalysisSectorSort({ sortBy, order }))
  }

  // aggregation type
  changeAggregationAnalysisType(aggType: EAggregationAnalysisType): void {
    this.store$.dispatch(AGGREGATION_ANALYSIS_ACTIONS.setAggregationAnalysisType({ aggType }))
  }
}
